import React, { useState, useEffect, useRef } from 'react';
import axios from '../Axiosinstance';
import {
  Container,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Box,
  Fade,
  Grow,
  Slide
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';


function StudentSummary() {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [status, setstatus] = useState('');
  const [startDate, setstartDate] = useState('');
  const [endDate, setendDate] = useState('');
  const [course, setcourse] = useState('');
  const [gender, setgender] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalPage, setTotalPage] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [courseList, setCourseList] = useState([]);
  const tableContainerRef = useRef(null);
  const [filtersChanged, setFiltersChanged] = useState(false);


  const buildQueryParams = () => {
    let params = {
      page,
      size: rowsPerPage,
    };

    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (gender) params.gender = gender;
    if (course) params.course = course;
    if (status) params.status = status;

    return params;
  };

  const fetchRegistrationData = async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {

      const response = await axios.get('/registrationadvances/studentSummary', {
        params: buildQueryParams(),
      });
      const { content, totalPages } = response.data;

      setData((prevData) => (page === 0 ? content : [...prevData, ...content]));
      setTotalPage(totalPages);
      setHasMore(page + 1 < totalPages); // Check if more pages exist
    } catch (error) {
      console.error('Error fetching registration data:', error);
    } finally {
      setIsLoading(false);
    }
  };


const handleClearFilters = () => {
  setstatus('');
  setstartDate('');
  setendDate('');
  setcourse('');
  setgender('');
  setData([]);
  setHasMore(true);
  setPage(0);
  setFiltersChanged(prev => !prev); // Flip to re-trigger useEffect
};


 const handleFilterSubmit = () => {
  setData([]);
  setHasMore(true);
  setPage(0); // This will trigger the useEffect below
  setFiltersChanged(prev => !prev); // Flip the flag to re-run effect
};





  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollPosition = container.scrollTop + container.clientHeight;
      const scrollHeight = container.scrollHeight;

      if (scrollHeight - scrollPosition <= 50 && !isLoading && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isLoading, hasMore]);


useEffect(() => {
  fetchRegistrationData();
}, [page, filtersChanged]);


 

  const downloadExcel = async () => {
    try {
      setIsDownloading(true); // Start the loader
      const params = {
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(gender && { gender }),
        ...(course && { course }),
        ...(status && { status }),
      };

      const response = await axios.get('/registrationadvances/studentSummary/export', {
        params, 
        responseType: 'blob', 
      });

      // Create a Blob from the response data
      const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Get the current date and time
      const now = new Date();

      // Format the date as ddmmyyyy and time as HHmm
      const formattedDate = now.toLocaleDateString('en-GB').split('/').reverse().join('');
      const formattedTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }).replace(':', '');

      // Create the file name with the formatted date and time
      const fileName = `Student_Summary_${formattedDate}_${formattedTime}.xlsx`;

      // Create a link element to download the file
      const link = document.createElement('a');
      link.href = URL.createObjectURL(file);
      link.download = fileName; // Set the dynamic file name
      link.click(); // Trigger the download
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    } finally {
      setIsDownloading(false); // Stop the loader
    }
  };


  const fetchCourseList = async () => {
    try {
      const response = await axios.get('/batch-summary');
      setCourseList(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };
  useEffect(() => { fetchCourseList(); }, []);

  return (
    <>

      <Container style={{ paddingBottom: '20px' }}>

        <Fade in timeout={800}>
          <Typography variant="h4" gutterBottom align="center" sx={{ color: 'primary.main',fontWeight: 'bold' }}>
            Student Summary
          </Typography>
        </Fade>
        <Box
          sx={{
            position: 'fixed',
            top: '100px', 
            left: '20px', 
            zIndex: 1000 
          }}
        >

        </Box>


        <Box my={2}>
          <Paper elevation={4} sx={{ p: 3, borderRadius: 3, background: '#f7f9fc' }}>
            <Grid container spacing={3} justifyContent="center">


              <Grid item xs={12} md={2.4}>
                <TextField
                  label="Start Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  value={startDate}
                  onChange={(e) => setstartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={2.4}>
                <TextField
                  label="End Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  value={endDate}
                  onChange={(e) => setendDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={2.4}>
              <TextField select label="Course" variant="outlined" fullWidth
                value={course}
                onChange={(e) => setcourse( e.target.value )}>
                {courseList.map((item) => (
                  <MenuItem key={item.id} value={item.course}>{item.course}</MenuItem>
                ))}
              </TextField>
            </Grid>
              <Grid item xs={12} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => setstatus(e.target.value)}
                  >
                    
                    <MenuItem value="Active"> Active</MenuItem>
                    <MenuItem value="withdrawn"> withdrawn</MenuItem>
                    <MenuItem value="Alumni"> Graduation</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={gender}
                    label="Gender"
                    onChange={(e) => setgender(e.target.value)}
                  >
                    
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>


            </Grid>

            <Box display="flex" justifyContent="center" mt={3}>
              <Grid container spacing={2} justifyContent="center">
                <Grow in timeout={400}>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleFilterSubmit}
                      startIcon={<FilterListIcon />}
                    >
                      Apply Filters
                    </Button>
                  </Grid>
                </Grow>

                <Grow in timeout={400}>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleClearFilters}
                      startIcon={<ClearIcon />}
                    >
                      Clear Filters
                    </Button>
                  </Grid>
                </Grow>

                <Grow in timeout={400}>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={downloadExcel}
                      startIcon={isDownloading ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                      }}
                      disabled={isDownloading}
                    >
                      {isDownloading ? 'Downloading...' : 'Download Excel'}
                    </Button>
                  </Grid>
                </Grow>
              </Grid>
            </Box>
          </Paper>
        </Box>


        <Fade in timeout={800}>
          <TableContainer
            component={Paper}
            ref={tableContainerRef}
            style={{ marginTop: 20, maxHeight: '500px', overflowY: 'auto' }}
          >
            <Table >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.300', zIndex: 1, fontWeight: 'bold' }}>S.No</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.300', zIndex: 1, fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.300', zIndex: 1, fontWeight: 'bold' }}>StudentId</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.300', zIndex: 1, fontWeight: 'bold' }}>NRIC/FIN</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.300', zIndex: 1, fontWeight: 'bold' }}>Gender</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.300', zIndex: 1, fontWeight: 'bold' }}>CourseTitle</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.300', zIndex: 1, fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: 'grey.300', zIndex: 1, fontWeight: 'bold' }}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.studentCustomId}</TableCell>
                    <TableCell>{(row.nric)}</TableCell>
                    <TableCell>{(row.gender)}</TableCell>
                    <TableCell>{(row.courseTitle)}</TableCell>
                    <TableCell>{(row.email)}</TableCell>
                    <TableCell>{(row.status)}</TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Fade>
        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}

        {!hasMore && !isLoading && (
          <Fade in timeout={400}>
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
            
              <Typography variant="h6" color="textSecondary">
                No more data to load.
              </Typography>
            </div>
          </Fade>
        )}

      </Container>
    </>
  );
}

export default StudentSummary;;